import React, { useState, useEffect, useRef, useCallback } from "react";
import { InputBase } from "../react-input/input-base/InputBase";
import {
  PropelOptionsList,
  PropelSelect,
  PropelSelectitem,
  DownArrowIcon,
} from "./Select.styles";
import { SelectProps } from "./Select.types";

// Custom hook for handling window resize
const useWindowSize = () => {
  const isClient = typeof window === "object";
  const [windowSize, setWindowSize] = useState({
    height: isClient ? window.innerHeight : undefined,
    width: isClient ? window.innerWidth : undefined,
  });

  useEffect(() => {
    if (!isClient) return;
    const handleResize = () => {
      setWindowSize({
        height: window.innerHeight,
        width: window.innerWidth,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isClient]);

  return windowSize;
};

// Custom hook for managing input value and options
const useInputValue = (initialValue: string, options: string[], onInputValueChange: any, keyProp: string) => {
  const [currentValue, setCurrentValue] = useState<string>(initialValue);

  useEffect(() => {
    setCurrentValue(initialValue);
  }, [initialValue]);

  const onInputChange = (value: string) => {
    setCurrentValue(value);
    const foundOption = options.find((option) => option.toLowerCase() === value.toLowerCase());
    onInputValueChange(foundOption ?? "", keyProp);
  };

  return [currentValue, onInputChange] as const;
};

// Custom hook for managing option visibility
const useOptionsVisibility = () => {
  const [optionsVisible, setOptionsVisible] = useState<boolean>(false);
  const showOptions = () => setOptionsVisible(true);
  const hideOptions = () => setOptionsVisible(false);

  return [optionsVisible, showOptions, hideOptions] as const;
};

export const Select = <T extends object>(props: SelectProps<T>) => {
  const {
    onInputValueChange = () => true,
    componentAccessId,
    options,
    noOptionsFoundLabel,
    keyProp,
    initialValue,
    placeHolder,
    requiredValidator,
    inputValidator,
    useLastSelectedOption,
    optionsIcon,
    isRequired,
  } = props;

  const windowSize = useWindowSize();
  const optionsReference = useRef<HTMLDivElement>(null);

  const [currentValue, onInputChange] = useInputValue(initialValue ?? "", options, onInputValueChange, keyProp as string);
  const [optionsVisible, showOptions, hideOptions] = useOptionsVisibility();

  const onBlur = () => {
    if (!currentValue && !useLastSelectedOption) {
      onInputChange("");
    }
    hideOptions();
  };

  const onOptionClick = useCallback(
    (option: string, index: number) => (e: React.MouseEvent) => {
      e.preventDefault();
      onInputChange(option);
      hideOptions();
      onInputValueChange(option, keyProp, index);
    },
    [onInputChange, hideOptions, onInputValueChange, keyProp]
  );

  const renderSingleOption = (option: string, index: number) => (
    <PropelSelectitem key={`drop-${option}-${index}`} onMouseDown={onOptionClick(option, index)}>
      {optionsIcon && optionsIcon[index] && (
        <div>
          <img src={optionsIcon[index]} alt="icon" />
        </div>
      )}
      <div>{option}</div>
    </PropelSelectitem>
  );

  const renderOptions = () => {
    const filteredOptions = options.filter((option) => option.toLowerCase().includes(currentValue.toLowerCase()));

    if (filteredOptions.length === 0) {
      return (
        <PropelOptionsList className="options-dropdown">
          <PropelSelectitem>
            <span className="no-options-found" style={{ color: "#9e9e9e", fontSize: "0.8rem" }}>
              {noOptionsFoundLabel}
            </span>
          </PropelSelectitem>
        </PropelOptionsList>
      );
    }

    return <PropelOptionsList className="material-dropdown">{filteredOptions.map(renderSingleOption)}</PropelOptionsList>;
  };

  const renderArrowIcon = () => <DownArrowIcon reverse={optionsVisible} />;

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      const filteredOptions = options.filter((option) => option.toLowerCase().includes(currentValue.toLowerCase()));
      if (filteredOptions.length === 1) {
        event.preventDefault();
        event.stopPropagation();
        onInputChange(filteredOptions[0]);
        hideOptions();
      }
    }
  };

  return (
    <PropelSelect data-cy={componentAccessId}>
      <InputBase<any>
        {...props}
        onBlur={onBlur}
        onFocus={showOptions}
        onClick={showOptions}
        inputValue={currentValue}
        onInputValueChange={onInputChange}
        floatingComponent={renderArrowIcon()}
        placeHolder={placeHolder}
        isRequired={isRequired}
        requiredValidator={requiredValidator}
        inputValidator={inputValidator}
        isReadOnly={!!(windowSize.width && windowSize.width < 769)}
        onKeyDown={onKeyDown}
      />
      <div ref={optionsReference}>{optionsVisible && renderOptions()}</div>
    </PropelSelect>
  );
};
